import { X } from 'lucide-react';
import { useLayerRegistry } from '@/map/layers/registry';
import type { LayerLegend, LegendSwatchKind } from '@/map/layers/types';

export function LegendOverlay() {
  const records = useLayerRegistry((s) => s.records);
  const legends = records
    .filter((r) => r.legend && r.legend.entries.length > 0)
    .map((r) => ({ layerId: r.id, legend: r.legend as LayerLegend }));

  if (legends.length === 0) return null;

  return (
    <div className="pointer-events-none absolute bottom-4 right-4 z-30 flex max-h-[calc(100%-2rem)] flex-col gap-2 overflow-y-auto">
      {legends.map(({ layerId, legend }) => (
        <LegendCard key={layerId} layerId={layerId} legend={legend} />
      ))}
    </div>
  );
}

function LegendCard({
  layerId,
  legend,
}: {
  layerId: string;
  legend: LayerLegend;
}) {
  const dismiss = () =>
    useLayerRegistry.getState().patch(layerId, { legend: undefined });

  return (
    <div className="pointer-events-auto min-w-[180px] max-w-[260px] rounded-md border border-border bg-card/95 p-2.5 shadow-md backdrop-blur">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <div className="truncate text-[11px] font-semibold text-foreground" title={layerId}>
          {layerId}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span
            className="truncate text-[10px] uppercase tracking-wider text-muted-foreground"
            title={legend.field}
          >
            {legend.field}
          </span>
          <button
            type="button"
            onClick={dismiss}
            className="grid size-4 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close legend"
            title="Close legend"
          >
            <X className="size-3" />
          </button>
        </div>
      </div>
      <ul className="flex flex-col gap-1">
        {legend.entries.map((e) => (
          <li key={e.index} className="flex items-center gap-2 text-[11px]">
            <Swatch kind={e.kind} color={e.color} />
            <span className="truncate" title={e.label}>
              {e.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Swatch({ kind, color }: { kind: LegendSwatchKind; color: string }) {
  if (kind === 'line') {
    return (
      <span
        className="block h-1.5 w-4 shrink-0 rounded-sm"
        style={{ backgroundColor: color }}
      />
    );
  }
  if (kind === 'point') {
    return (
      <span
        className="block size-3 shrink-0 rounded-full border border-white/60"
        style={{ backgroundColor: color }}
      />
    );
  }
  return (
    <span
      className="block size-3.5 shrink-0 rounded-sm border"
      style={{ backgroundColor: color + '66', borderColor: color }}
    />
  );
}
